import React, { memo, forwardRef, useCallback, useContext, useMemo } from 'react';
import cn from 'classnames';
import { CheckIcon } from '@gwc/icons';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { usePatchedFocusRing } from '../../hooks';
import styles from './styles.module.css';
export const RadioGroupContext = React.createContext(null);
export const RadioBase = memo(forwardRef(function RadioBase({ disabled, value, size = 'm', hovered, error, focused, className, labelId, checked, checkIcon = React.createElement(CheckIcon, null), name, onChange, ...rest }, ref) {
    return (React.createElement("div", { className: cn(className, styles.radio, styles[size], {
            [styles.disabled]: disabled,
            [styles.hovered]: hovered,
            [styles.error]: error,
            [styles.focused]: focused,
            [styles.checked]: checked,
        }), ref: ref, ...rest },
        !!checked ? checkIcon : undefined,
        React.createElement("input", { id: labelId, name: name, type: "radio", value: onChange ? value : undefined, onChange: disabled ? undefined : onChange, className: styles.hiddenInput, readOnly: disabled || !onChange, disabled: disabled || !onChange, tabIndex: disabled ? -1 : undefined, checked: onChange ? checked : undefined })));
}));
export function useRadio(props) {
    const groupContext = useContext(RadioGroupContext);
    if (!groupContext) {
        throw new Error('Radio should be wrapped into RadioGroup');
    }
    const { disabled } = props;
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    const { isFocusVisible, focusProps } = usePatchedFocusRing({ within: true });
    const { name, checkIcon, size } = groupContext;
    const onChange = useCallback((e) => {
        var _a;
        (_a = groupContext.onChange) === null || _a === void 0 ? void 0 : _a.call(groupContext, e.currentTarget.value, e);
    }, [groupContext.onChange]);
    return mergeProps(props, { name, checkIcon, onChange, hovered: isHovered, focused: isFocusVisible, size }, hoverProps, focusProps);
}
export const Radio = memo(forwardRef(function Radio(props, ref) {
    const radioProps = useRadio(props);
    return React.createElement(RadioBase, { ref: ref, ...radioProps });
}));
const checkIcon = React.createElement(CheckIcon, null);
export const RadioGroup = memo(function RadioGroup({ children, name, onChange, size, }) {
    const context = useMemo(() => ({ name, checkIcon, onChange, size }), [name, onChange, size]);
    return React.createElement(RadioGroupContext.Provider, { value: context }, children);
});
